import { useState, type FormEvent } from "react";
import { login, register } from "../lib/auth";
import { useConfig } from "../lib/config";
import { navigate } from "../lib/router";
import { SceneFallback } from "../ui";

// Lagoon AuthGate — mascot retired per Phase 0 decision. The identity
// mark is now the Fraunces wordmark on a small SceneFallback panel; the
// caustic recipe from Phase 2 does the atmospheric work without the
// hex-heavy custom SVG.

export function AuthGate() {
  const cfg = useConfig();
  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [verifySent, setVerifySent] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email.trim(), password);
      } else {
        const r = await register(email.trim(), password);
        if (r.verificationPending) {
          setVerifySent(email.trim());
          setBusy(false);
          return;
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
      setBusy(false);
    }
  };

  if (verifySent) {
    return (
      <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-sand px-4 text-center">
        <div className="w-full max-w-sm rounded-2xl bg-paper p-6 shadow-card ring-1 ring-inset ring-ring">
          <h1 className="font-display text-2xl font-semibold text-ink">
            Check your email
          </h1>
          <p className="mt-3 text-sm text-meta">
            If <span className="font-semibold text-ink">{verifySent}</span> is a
            valid address, we've sent it a link. Open the link to finish
            setting up your account and sign in.
          </p>
          <p className="mt-3 text-xs text-meta">
            Didn't get it? Check your spam folder, then{" "}
            <button
              type="button"
              onClick={() => setVerifySent(null)}
              className="font-semibold text-aqua-deep underline"
            >
              try again
            </button>
            .
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-sand px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <SceneFallback
            variant="lagoon"
            silhouette="fish1"
            className="h-14 w-14 rounded-2xl shadow-card"
            silhouetteClass="w-9 left-[10px] top-[14px]"
            vignette={false}
          />
          <h1 className="font-display text-2xl font-semibold text-ink">
            Reefscope
          </h1>
          <p className="text-sm text-meta">
            {mode === "login"
              ? "Sign in to sync your lifelist across devices."
              : "Create an account to save your lifelist."}
          </p>
        </div>

        <form
          onSubmit={submit}
          className="flex flex-col gap-3 rounded-2xl bg-paper p-5 shadow-card ring-1 ring-inset ring-ring"
        >
          <label className="flex flex-col gap-1 text-sm font-semibold text-ink">
            Email
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-xl bg-sand px-3 py-2 text-base font-normal text-ink ring-1 ring-inset ring-ring focus:outline-none focus:ring-2 focus:ring-aqua-deep"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm font-semibold text-ink">
            Password
            <input
              type="password"
              autoComplete={
                mode === "login" ? "current-password" : "new-password"
              }
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-xl bg-sand px-3 py-2 text-base font-normal text-ink ring-1 ring-inset ring-ring focus:outline-none focus:ring-2 focus:ring-aqua-deep"
            />
            {mode === "register" && (
              <span className="text-xs font-normal text-meta">
                At least 8 characters.
              </span>
            )}
          </label>

          {error && (
            <p
              role="alert"
              className="rounded-xl bg-coral/10 px-3 py-2 text-sm text-coral"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="btn-primary mt-1 min-h-[44px] text-sm"
          >
            {busy
              ? "Please wait…"
              : mode === "login"
                ? "Sign in"
                : "Create account"}
          </button>

          {mode === "register" && (
            <p className="mt-1 text-center text-xs text-meta">
              By creating an account you agree to our{" "}
              <button
                type="button"
                onClick={() => navigate("/terms")}
                className="underline hover:text-ink"
              >
                Terms
              </button>{" "}
              and{" "}
              <button
                type="button"
                onClick={() => navigate("/privacy")}
                className="underline hover:text-ink"
              >
                Privacy Policy
              </button>
              .
            </p>
          )}
        </form>

        {mode === "login" && cfg.email_available && (
          <p className="mt-3 text-center text-sm">
            <button
              type="button"
              onClick={() => navigate("/forgot-password")}
              className="text-meta hover:text-aqua-deep hover:underline"
            >
              Forgot your password?
            </button>
          </p>
        )}

        <p className="mt-4 text-center text-sm text-meta">
          {mode === "login" ? "New here?" : "Already have an account?"}{" "}
          <button
            type="button"
            onClick={() => {
              setMode(mode === "login" ? "register" : "login");
              setError(null);
            }}
            className="font-bold text-aqua-deep hover:underline"
          >
            {mode === "login" ? "Create an account" : "Sign in"}
          </button>
        </p>

        <p className="mt-6 text-center text-xs text-meta/70">
          <button
            type="button"
            onClick={() => navigate("/privacy")}
            className="hover:text-ink"
          >
            Privacy
          </button>
          {" · "}
          <button
            type="button"
            onClick={() => navigate("/terms")}
            className="hover:text-ink"
          >
            Terms
          </button>
          {" · "}
          <button
            type="button"
            onClick={() => navigate("/cookies")}
            className="hover:text-ink"
          >
            Cookies
          </button>
        </p>
      </div>
    </div>
  );
}
